import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

// Four-card first-visit intro — Founder plain-ink 2026-07-02+:
// "Ok let's see the four-card ... Bake it."
//
// Fires once, ever, on the first hit of /products. Fades in on top of the
// catalog. Dismisses forever on any of: Skip, Esc, backdrop click, or the
// final "Enter the catalog" tap. Sets localStorage.rs_products_intro_seen=1
// so it never re-appears.

const CARDS = [
  {
    kicker: "01 · Story",
    headline: "Every world starts with a sentence.",
    body: "Logline. Tagline. Paragraph pitch. Treatment. Screenplay. Story-level commissions start at $99 and scale through a $12K full screenplay — the same brief, drafted, revised, delivered.",
  },
  {
    kicker: "02 · Screen",
    headline: "Every screenplay wants to move.",
    body: "Visual development. Motion clips. Full animated features. Boutique-tier work at fractions of the traditional cost — backed by our own hero reel, built on the same rail.",
  },
  {
    kicker: "03 · Music",
    headline: "Every scene wants a score.",
    body: "Cue packs. Voice cast. Feature score. Signature sound package. ElevenLabs orchestral rail. Nine percent artist royalty on signings — the industry floor is fifteen to fifty.",
  },
  {
    kicker: "04 · Web",
    headline: "Every idea wants a door.",
    body: "Websites. Ad campaigns. Marketing packages. Books. eCourses. Pitch decks. The DIY-first footer on every product links to Sage if you would rather drive the tool yourself.",
  },
];

export default function ProductsIntro() {
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    try {
      if (!localStorage.getItem("rs_products_intro_seen")) {
        setVisible(true);
      }
    } catch (_) {
      /* SSR / private mode — skip */
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
      if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, CARDS.length - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  const dismiss = () => {
    try { localStorage.setItem("rs_products_intro_seen", "1"); } catch (_) {}
    setVisible(false);
  };

  if (!visible) return null;

  const card = CARDS[idx];
  const isLast = idx === CARDS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        key="products-intro"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        data-testid="products-intro"
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95 backdrop-blur-lg px-4 py-10"
        onClick={(e) => e.target === e.currentTarget && dismiss()}
      >
        <div className="relative w-full max-w-3xl">
          {/* Skip button — top right */}
          <button
            type="button"
            onClick={dismiss}
            data-testid="products-intro-skip"
            aria-label="Skip intro"
            className="absolute -top-2 right-0 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] hover:text-[#ededed] transition-colors"
          >
            Skip <X className="w-3.5 h-3.5" />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`products-intro-card-${idx}`}
              className="pt-16 pb-4"
            >
              <p
                className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-6"
                data-testid={`products-intro-kicker-${idx}`}
              >
                {card.kicker}
              </p>
              <h2
                className="font-display text-[#ededed] leading-[0.95]"
                style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
                data-testid={`products-intro-headline-${idx}`}
              >
                {card.headline}
              </h2>
              <p
                className="font-body text-lg sm:text-xl text-[#d4d4d8] leading-[1.7] mt-8 max-w-2xl"
                data-testid={`products-intro-body-${idx}`}
              >
                {card.body}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Controls */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            {/* Progress dots */}
            <div className="flex items-center gap-2" data-testid="products-intro-dots">
              {CARDS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  data-testid={`products-intro-dot-${i}`}
                  aria-label={`Go to card ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx
                      ? "w-8 bg-[#ff5a00]"
                      : "w-1.5 bg-white/25 hover:bg-white/45"
                  }`}
                />
              ))}
            </div>

            {/* Nav */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIdx((i) => Math.max(i - 1, 0))}
                disabled={idx === 0}
                data-testid="products-intro-prev"
                aria-label="Previous card"
                className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-white/40 text-[#a1a1aa] hover:text-[#ededed] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {!isLast && (
                <button
                  type="button"
                  onClick={() => setIdx((i) => Math.min(i + 1, CARDS.length - 1))}
                  data-testid="products-intro-next"
                  aria-label="Next card"
                  className="w-9 h-9 flex items-center justify-center border border-[#ff5a00]/60 hover:border-[#ff5a00] text-[#ff5a00] transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
              {isLast && (
                <button
                  type="button"
                  onClick={dismiss}
                  data-testid="products-intro-enter"
                  className="px-5 py-2 font-mono text-[10px] uppercase tracking-[0.32em] bg-[#ff5a00] text-[#050505] hover:bg-[#ff8f00] transition-colors"
                >
                  Enter the catalog
                </button>
              )}
            </div>
          </div>

          {/* Close-line under all four */}
          <p
            className="font-mono text-[9px] uppercase tracking-[0.32em] text-[#71717a] mt-6 leading-[1.7]"
            data-testid="products-intro-close-line"
          >
            Pick a door. Or tap the ribbon in the corner —
            <span className="text-[#ff5a00]"> Bug Hunter </span>
            earns you $5 off, stacked as one coupon, applied automatically.
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
